<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <oxd-dialog
    class="orangehrm-dialog-modal attendance-period-details-dialog"
    :style="{width: '92%', maxWidth: '980px'}"
    @update:show="onClose"
  >
    <div
      class="orangehrm-modal-header attendance-period-details-dialog__header"
    >
      <oxd-text type="card-title">{{ employeeName }}</oxd-text>
      <oxd-text tag="p" class="attendance-period-details-dialog__period">
        {{ periodLabel }}
      </oxd-text>
    </div>
    <oxd-divider />
    <div class="attendance-period-details-dialog__toolbar">
      <button
        type="button"
        class="attendance-period-details-dialog__toggle"
        :class="{
          'attendance-period-details-dialog__toggle--active':
            viewMode === 'fraction',
        }"
        @click="viewMode = 'fraction'"
      >
        Work fraction
      </button>
      <button
        type="button"
        class="attendance-period-details-dialog__toggle"
        :class="{
          'attendance-period-details-dialog__toggle--active':
            viewMode === 'hours',
        }"
        @click="viewMode = 'hours'"
      >
        Hours
      </button>
    </div>
    <div class="attendance-period-details-dialog__scroll">
      <table class="attendance-period-details-dialog__table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Day</th>
            <th>Clock In</th>
            <th>Clock Out</th>
            <th>Day Type</th>
            <template v-if="viewMode === 'fraction'">
              <th class="is-numeric">WF</th>
              <th class="is-numeric">LF</th>
              <th class="is-numeric">HF</th>
              <th class="is-numeric">PF</th>
            </template>
            <th v-else class="is-numeric">Duration (Hours)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.date">
            <td>{{ row.date }}</td>
            <td>{{ row.dayName }}</td>
            <td>{{ row.punchIn }}</td>
            <td>{{ row.punchOut }}</td>
            <td>
              <span
                v-for="(chip, chipIndex) in row.dayTypeChips"
                :key="chipIndex"
                :class="chip.style ? 'day-type-chip' : null"
                :style="chip.style"
              >
                {{ chip.text }}
              </span>
            </td>
            <template v-if="viewMode === 'fraction'">
              <td class="is-numeric">{{ row.attendanceFraction }}</td>
              <td class="is-numeric">{{ row.leaveFraction }}</td>
              <td class="is-numeric">{{ row.holidayFraction }}</td>
              <td class="is-numeric">{{ row.payableFraction }}</td>
            </template>
            <td v-else class="is-numeric">{{ row.durationHours }}</td>
          </tr>
          <tr class="attendance-period-details-dialog__total">
            <td>Total</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <template v-if="viewMode === 'fraction'">
              <td class="is-numeric">{{ totals.attendanceFraction }}</td>
              <td class="is-numeric">{{ totals.leaveFraction }}</td>
              <td class="is-numeric">{{ totals.holidayFraction }}</td>
              <td class="is-numeric">{{ totals.payableFraction }}</td>
            </template>
            <td v-else class="is-numeric">{{ totals.durationHours }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </oxd-dialog>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';
import moment from 'moment';

function formatBrowserDate(iso) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return iso || '';
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return date.toLocaleDateString();
}

function round2(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function formatFraction(value) {
  const rounded = round2(value);
  if (rounded === 0) {
    return '0';
  }
  return String(rounded);
}

function formatHours(value) {
  return round2(value).toFixed(2);
}

function leaveAbbreviation(entry) {
  const leaveName = entry.leave?.name || '';
  const text = (entry.leave?.abbreviation || '').trim();
  if (text) {
    return text;
  }
  const fromSlash = [];
  leaveName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .forEach((word) => {
      word.split('/').forEach((segment) => {
        const piece = segment.trim();
        if (piece.length) {
          fromSlash.push(piece.charAt(0).toUpperCase());
        }
      });
    });
  return fromSlash.join('').slice(0, 10);
}

function clockTimes(entry) {
  const punches = Array.isArray(entry.attendances) ? entry.attendances : [];
  const punchIn = punches
    .map((punch) => punch.punchInTime)
    .filter(Boolean)
    .join(', ');
  const punchOut = punches
    .map((punch) => punch.punchOutTime)
    .filter(Boolean)
    .join(', ');
  return {
    punchIn: punchIn || '-',
    punchOut: punchOut || '-',
  };
}

function durationHours(entry) {
  if (entry.workHours != null) {
    return Number(entry.workHours) || 0;
  }
  const punches = Array.isArray(entry.attendances) ? entry.attendances : [];
  return punches.reduce(
    (sum, punch) => sum + (Number(punch.workHours) || 0),
    0,
  );
}

function dayTypeLabel(entry, payroll, isFuture) {
  const parts = [];
  const holiday = Number(payroll.holiday) || 0;
  const paidLeave =
    (Number(payroll.annual) || 0) +
    (Number(payroll.sick) || 0) +
    (Number(payroll.permission) || 0);
  const absence = Number(payroll.absence) || 0;
  const attendance = Number(payroll.attendance) || 0;
  const nonWorking =
    payroll.nonWorking === true || entry.isWorkWeekDay === false;
  if (holiday > 0) {
    parts.push('PH');
  }
  if (entry.leave?.name && (paidLeave > 0 || isFuture)) {
    const mark = leaveAbbreviation(entry);
    if (mark) {
      parts.push(mark);
    }
  }
  if (nonWorking) {
    parts.push('OFF');
  }
  if (absence > 0 && parts.length === 0) {
    parts.push('ABS');
  }
  if (attendance > 0 && parts.length === 0) {
    parts.push('WORKDAY');
  }
  return parts.join(', ');
}

const MARK_CHIP_STYLES = {
  OFF: {backgroundColor: '#deecff', color: '#1e3a5f'},
  ABS: {backgroundColor: '#ffe5e5', color: '#9f1239'},
  PH: {backgroundColor: '#ddf0e2', color: '#14532d'},
  SL: {backgroundColor: '#fde6e6', color: '#991b1b'},
  AL: {backgroundColor: '#dbf1d9', color: '#14532d'},
};

function normalizeHexColor(value) {
  if (value == null || String(value).trim() === '') {
    return null;
  }
  let hex = String(value).trim();
  if (hex[0] !== '#') {
    hex = `#${hex}`;
  }
  if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }
  return hex.toUpperCase();
}

function dayTypeChips(entry, label) {
  if (!label) {
    return [];
  }
  const leaveMark = leaveAbbreviation(entry);
  const leaveBg = normalizeHexColor(entry.leave?.color);
  const leaveFg = normalizeHexColor(entry.leave?.foregroundColor) || '#374151';
  return label
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean)
    .map((text) => {
      let style = null;
      if (leaveBg && text === leaveMark) {
        style = {backgroundColor: leaveBg, color: leaveFg};
      } else if (MARK_CHIP_STYLES[text]) {
        style = MARK_CHIP_STYLES[text];
      } else if (text !== 'WORKDAY') {
        style = MARK_CHIP_STYLES.AL;
      }
      return {text, style};
    });
}

function buildPeriodRows(entries) {
  const today = moment().format('YYYY-MM-DD');
  const totals = {
    attendanceFraction: 0,
    leaveFraction: 0,
    holidayFraction: 0,
    payableFraction: 0,
    durationHours: 0,
  };
  const rows = (entries || [])
    .filter((entry) => entry?.date)
    .slice()
    .sort((left, right) => String(left.date).localeCompare(String(right.date)))
    .map((entry) => {
      const payroll = entry.payroll || {};
      const isFuture = entry.date > today;
      const attendance = Number(payroll.attendance) || 0;
      const leave =
        (Number(payroll.annual) || 0) +
        (Number(payroll.sick) || 0) +
        (Number(payroll.permission) || 0);
      const holiday = Number(payroll.holiday) || 0;
      const payable = round2(
        Math.min(1, Math.max(0, attendance + leave + holiday)),
      );
      const hours = durationHours(entry);
      const clocks = clockTimes(entry);
      const dayType = dayTypeLabel(entry, payroll, isFuture);
      if (entry.date <= today && !payroll.skipped) {
        totals.attendanceFraction += attendance;
        totals.leaveFraction += leave;
        totals.holidayFraction += holiday;
        totals.payableFraction += payable;
        totals.durationHours += hours;
      }
      return {
        date: formatBrowserDate(entry.date),
        dayName: moment(entry.date).format('ddd').toUpperCase(),
        punchIn: clocks.punchIn,
        punchOut: clocks.punchOut,
        dayTypeChips: dayTypeChips(entry, dayType),
        attendanceFraction: formatFraction(attendance),
        leaveFraction: formatFraction(leave),
        holidayFraction: formatFraction(holiday),
        payableFraction: formatFraction(payable),
        durationHours: formatHours(hours),
      };
    });
  return {
    rows,
    totals: {
      attendanceFraction: formatFraction(totals.attendanceFraction),
      leaveFraction: formatFraction(totals.leaveFraction),
      holidayFraction: formatFraction(totals.holidayFraction),
      payableFraction: formatFraction(totals.payableFraction),
      durationHours: formatHours(totals.durationHours),
    },
  };
}

export default {
  name: 'AttendancePeriodDetailsDialog',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    employeeName: {
      type: String,
      default: '',
    },
    fromDate: {
      type: String,
      default: '',
    },
    toDate: {
      type: String,
      default: '',
    },
    entries: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close'],
  data() {
    return {
      viewMode: 'fraction',
    };
  },
  computed: {
    periodLabel() {
      const from = formatBrowserDate(this.fromDate);
      const to = formatBrowserDate(this.toDate);
      if (from && to) {
        return `${from} – ${to}`;
      }
      return from || to || '';
    },
    built() {
      return buildPeriodRows(this.entries);
    },
    rows() {
      return this.built.rows;
    },
    totals() {
      return this.built.totals;
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.attendance-period-details-dialog {
  &__period {
    margin: 0.25rem 0 0;
    color: $oxd-interface-gray-darken-1-color;
  }

  &__toolbar {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  &__toggle {
    border: 1px solid $oxd-interface-gray-lighten-1-color;
    background: #fff;
    border-radius: 1rem;
    padding: 0.25rem 0.75rem;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 0.75rem;
    cursor: pointer;

    &--active {
      background: $oxd-primary-one-color;
      border-color: $oxd-primary-one-color;
      color: #fff;
    }
  }

  &__scroll {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    max-height: 60vh;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    scrollbar-color: #9ca3af transparent;
    border: 1px solid $oxd-interface-gray-lighten-2-color;
    border-radius: 4px;
    box-sizing: border-box;
  }

  &__scroll::-webkit-scrollbar {
    height: 8px;
  }

  &__scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  &__scroll::-webkit-scrollbar-thumb {
    background-color: #9ca3af;
    border-radius: 4px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    background-color: $oxd-white-color;
    font-family: sans-serif;
    font-size: 12px;
    color: $oxd-interface-gray-darken-1-color;

    th,
    td {
      padding: 0.65rem 0.5rem;
      border-top: 1px solid $oxd-interface-gray-lighten-2-color;
      vertical-align: middle;
      white-space: nowrap;
      text-align: left;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 1;
      font-weight: 700;
      background-color: $oxd-background-light-gray-color;
      border-top: 0;
    }

    tbody tr:hover:not(.attendance-period-details-dialog__total) {
      background-color: $oxd-background-light-gray-color;
    }

    .is-numeric {
      text-align: right;
    }

    .day-type-chip {
      display: inline-block;
      margin: 0.1rem 0.25rem 0.1rem 0;
      padding: 0.05rem 0.45rem;
      border-radius: 4px;
      font-weight: 700;
      line-height: 1.5;
    }
  }

  &__total td {
    font-weight: 700;
    background-color: $oxd-background-light-gray-color;
  }
}
</style>
